/**
 * The authoritative BotDefaults shape: types, validation, and layer resolution
 * for trader-native bot default config (the `bot_defaults` JSONB on
 * organizations + users).
 *
 * This file is AUTHORED here and mirrored byte-for-byte to
 * relay/src/lib/bots/botDefaults.ts, which relay code imports. The relay and g1
 * are separate build roots — relay's tsc pins `rootDir: "src"`, so it cannot
 * reach a file outside relay/src, and there is no shared package — so a copy is
 * the only way across the boundary. relay/src/lib/bots/mirror.test.ts re-derives
 * this file and fails on ANY drift, which is what turns "keep the two in sync"
 * from a comment into an enforced invariant. Edit this file and re-copy; never
 * edit the mirror by hand.
 *
 * Deliberately IMPORT-FREE. Zero imports is what lets the mirror be byte
 * identical rather than sed-derived: relay is ESM at runtime, so any relative
 * import here would need a `.js` extension on that side only — see
 * relay/src/lib/mm-defense/mirror.test.ts for the transform this avoids.
 *
 * The types live here rather than in relay/src/db/schema.ts because that file
 * pulls in the whole drizzle pgTable graph, and the editor needs the same shape.
 * Note the import-free rule is not what keeps drizzle out of the client bundle —
 * drizzle-orm is declared only in relay/package.json and isn't resolvable from
 * the repo root, so a `src/` file importing it fails to resolve in vite and tsc
 * alike. That protection is structural; this rule is about byte-identity.
 *
 * Kept defensive: bad payloads are rejected so the JSONB never holds garbage,
 * and unknown top-level keys are dropped.
 *
 * Resolution order at spawn time is personal → org → edge built-in, per field;
 * an unset field falls through to the next layer. The actual built-in defaults
 * live in the edge strategy (g10edge VanillaConfig / QueueRecyclerConfig); g1
 * only carries the org + personal override layers, so resolution here merges
 * those two and leaves unset fields for the edge to fill with its own defaults.
 */

/** One edge bot kind's default knobs (mirrors the g10edge strategy config). */
export type BotKindConfig = Record<string, number>;

export interface BotDefaults {
  /** Per-kind default config (the active values at this scope). */
  byKind?: Partial<Record<string, BotKindConfig>>;
  /** Named profile library (org scope): label → per-kind config. */
  profiles?: Record<string, Partial<Record<string, BotKindConfig>>>;
  /** The active profile label, when profiles are in use. */
  profile?: string;
}

function isFiniteNumber(x: unknown): x is number {
  return typeof x === 'number' && Number.isFinite(x);
}

function isPlainObject(x: unknown): x is Record<string, unknown> {
  return !!x && typeof x === 'object' && !Array.isArray(x);
}

function isKindConfig(x: unknown): x is BotKindConfig {
  // Every bot-config value is a magnitude/count/threshold (contracts, ¢
  // distances, caps, fill-avoidance) — all non-negative. Rejecting negatives
  // here stops a hand-crafted PATCH (past the UI) from pushing a negative into
  // the edge strategy.
  //
  // Note what is NOT checked: knob KEYS are unconstrained. An open record is
  // what lets a new bot knob ship without a DB migration, so a stored config
  // carrying a knob this build has never heard of stays valid and round-trips
  // untouched. Never tighten this to a known-key whitelist.
  return isPlainObject(x) && Object.values(x).every((v) => isFiniteNumber(v) && v >= 0);
}

/** A map of edge-kind → config (used by byKind and by each named profile). */
function isKindMap(x: unknown): boolean {
  return isPlainObject(x) && Object.values(x).every(isKindConfig);
}

/**
 * Validate a bot-defaults payload from an API body. Returns the (cleaned) value,
 * `null` to clear, or the string `'invalid'` for the caller to 400 on.
 */
export function parseBotDefaults(value: unknown): BotDefaults | null | 'invalid' {
  if (value === null || value === undefined) return null;
  if (!isPlainObject(value)) return 'invalid';

  if (value.byKind !== undefined && !isKindMap(value.byKind)) return 'invalid';
  if (value.profile !== undefined && typeof value.profile !== 'string') return 'invalid';
  if (value.profiles !== undefined) {
    if (!isPlainObject(value.profiles)) return 'invalid';
    if (!Object.values(value.profiles).every(isKindMap)) return 'invalid';
  }

  // Keep only known fields. An unrecognized TOP-LEVEL key is dropped, not
  // rejected — a payload carrying one still saves, it just doesn't persist that
  // key. (Contrast with unknown knob keys inside a kind, which are kept.)
  const out: BotDefaults = {};
  if (value.byKind !== undefined) out.byKind = value.byKind as BotDefaults['byKind'];
  if (value.profiles !== undefined) out.profiles = value.profiles as BotDefaults['profiles'];
  if (value.profile !== undefined) out.profile = value.profile as string;
  return out;
}

/**
 * Resolve the effective config for one kind across the personal → org layers
 * (a field set at the personal scope wins; otherwise the org value; otherwise
 * unset, left for the edge built-in). Org profiles are honored via the org's
 * active `profile` when present.
 *
 * Merged by spread, not `??`, so a stored `0` is a real value that overrides a
 * lower layer — 0 means "off" for these knobs, not "unset". Absence is the only
 * way to inherit.
 */
export function resolveBotConfig(
  kind: string,
  personal: BotDefaults | null | undefined,
  org: BotDefaults | null | undefined,
): BotKindConfig {
  const orgProfile = org?.profile && org.profiles?.[org.profile]?.[kind];
  const orgByKind = org?.byKind?.[kind];
  const personalByKind = personal?.byKind?.[kind];
  return { ...(orgProfile ?? {}), ...(orgByKind ?? {}), ...(personalByKind ?? {}) };
}
